import { useState } from "react";
import { ShoppingCart as CartIcon, Plus, Minus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function ShoppingCart({ cart, onUpdateQuantity, onRemoveItem, onClearCart, showTotal = true }) {
  const [isOpen, setIsOpen] = useState(false);

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <>
      {/* Floating Cart Button */}
      <div className="fixed bottom-2 right-4 z-50">
        <Button
          data-testid="cart-button"
          onClick={() => setIsOpen(true)}
          className="bg-blue-500/90 hover:bg-blue-600 backdrop-blur-sm text-white rounded-full w-[52px] h-[52px] shadow-lg hover:shadow-xl transition-all hover:scale-105 relative"
          size="lg"
        >
          <CartIcon className="w-5 h-5" />
          {totalItems > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-lg animate-pulse">
              {totalItems}
            </span>
          )}
        </Button>
      </div>

      {/* Cart Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-[95%] sm:max-w-xl max-h-[80vh] overflow-y-auto rounded-3xl" data-testid="cart-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CartIcon className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
              </div>
              Mi Pedido
            </DialogTitle>
          </DialogHeader>

          {cart.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <CartIcon className="w-12 h-12 text-gray-300" />
              </div>
              <p className="text-gray-500 text-lg font-medium">Tu carrito está vacío</p>
              <p className="text-gray-400 text-sm mt-2">Añade productos para crear tu pedido</p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Cart Items */}
              <div className="space-y-3">
                {cart.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`cart-item-${item.id}`}
                    className="border-2 border-gray-100 rounded-2xl hover:border-emerald-200 hover:bg-emerald-50/30 transition-all p-4"
                  >
                    <div className="flex gap-3 items-start">
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-20 h-20 flex-shrink-0 object-cover rounded-xl shadow-sm"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 text-base break-words pr-2">{item.name}</h4>
                        <p className="text-emerald-600 font-bold text-lg mt-1">{item.price.toFixed(2)}€</p>
                        {item.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2 break-words">{item.description}</p>
                        )}
                      </div>
                      <Button
                        data-testid={`remove-item-${item.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveItem(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <Button
                          data-testid={`decrease-${item.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                          className="w-9 h-9 p-0 rounded-xl border-2 hover:bg-emerald-50 hover:border-emerald-300 flex-shrink-0"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="font-bold w-8 text-center text-lg">{item.quantity}</span>
                        <Button
                          data-testid={`increase-${item.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                          className="w-9 h-9 p-0 rounded-xl border-2 hover:bg-emerald-50 hover:border-emerald-300 flex-shrink-0"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-base font-bold text-gray-900">
                        Total: {(item.price * item.quantity).toFixed(2)}€
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="border-t-2 border-gray-200 pt-5">
                {showTotal && (
                  <>
                    <div className="flex justify-between items-center mb-3 px-2">
                      <span className="text-base font-medium text-gray-600">Total de productos:</span>
                      <span className="text-lg font-bold text-gray-900 bg-gray-100 px-3 py-1 rounded-full">{totalItems}</span>
                    </div>
                    <div className="flex justify-between items-center mb-5 bg-emerald-50 rounded-2xl p-4">
                      <span className="text-2xl font-bold text-gray-900">Total:</span>
                      <span className="text-3xl font-bold text-emerald-600">{totalPrice.toFixed(2)}€</span>
                    </div>
                  </>
                )}
                <div className="flex gap-3">
                  <Button
                    data-testid="clear-cart-btn"
                    variant="outline"
                    onClick={onClearCart}
                    className="flex-1 h-12 rounded-xl border-2 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Vaciar
                  </Button>
                  <Button
                    data-testid="close-cart-btn"
                    onClick={() => setIsOpen(false)}
                    className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    Continuar Pidiendo
                  </Button>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-4 text-sm text-blue-800">
                <p className="font-bold mb-1 flex items-center gap-2">
                  ℹ️ Información:
                </p>
                <p className="text-blue-700">Este es un resumen de tu pedido. Muéstraselo al camarero para realizar tu pedido.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
